import * as echarts from "echarts";
import { default as EChartsReact, default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import ReactDomServer from "react-dom/server";
import {
  DataRow,
  EquipmentInteractionData,
  EquipmentReportBlock,
  ReportBlockFilters,
  SplitBy,
  Tab
} from "../../../../../stores/TabsStore";
import _ from "lodash";
import { hslColor } from "../../../../../enums/colors";
import { useAppStore } from "../../../../../stores/RootStore";
import { TbArrowLeft, TbArrowRight } from "react-icons/tb";
import { DateShift } from "../../../../../types/types";


const states = [
  { name: "Worked", value: "worked", color: "#157145" },
  { name: "Stopped", value: "stopped", color: "#fac858" },
  { name: "Turned off", value: "turned_off", color: "rgba(128,128,128,0.1)" },
];

const EGantt = ({
  data,
}: {
  data: DataRow[];
}) => {
  const [ref, setRef] = useState<EChartsReact | null>();
  const [loading, setLoading] = useState(true)
  const [filteredData, setFilteredData] = useState<DataRow[]>()

  const days: string[] = _.uniq(data.map(r => r.dateShift.start_day.toSQL()!))
  const shifts: DateShift[] = _.orderBy(_.uniqBy(data.map(r => r.dateShift), el => el.id), "start_time")

  const [grpahStart, setGraphStart] = useState<number>(0)
  const [grpahEnd, setGraphEnd] = useState<number>(86400)
  const [timelines, setTimelines] = useState<any[]>()
  const [shiftMarkers, setShiftMarkers] = useState<{name: string, xAxis: number }[]>([]);


  useEffect(() => {
    setLoading(true)
    setFilteredData(data)
  }, [])

  useEffect(() => {
    if(filteredData && filteredData.length > 0) {
      let first_shift_start = shifts[0]?.start_time.toSeconds()! - shifts[0]!.start_day.toSeconds()!;
      let last_shift_end = shifts[shifts.length - 1].end_time.toSeconds() - shifts[0]!.start_day.toSeconds()!;

      setShiftMarkers(shifts.map(shift => (
        [{name: `${t('start')} ${shift.label}`, xAxis: shift.start_time.toSeconds() - shift.start_day.toSeconds()}, {name: `${t('finish')} ${shift.label}`, xAxis: shift.end_time.toSeconds() - shift.start_day.toSeconds()}]
      )).flat())
      
      setGraphStart(first_shift_start)
      setGraphEnd(last_shift_end)

      let c = calculateData(filteredData!, days!)
      setTimelines(c)
      setLoading(false)
    }
  }, [filteredData])

  function renderItem(
    params: { coordSys: { x: any; y: any; width: any; height: any } },
    api: {
      value: (arg0: number) => any;
      coord: (arg0: any[]) => any;
      size: (arg0: number[]) => number[];
      style: () => any;
    }
  ) {
    let dayIndex = api.value(0);

    let start = api.coord([api.value(1), dayIndex]);
    let end = api.coord([api.value(2), dayIndex]);
    let height = api.size([0, 1])[1] * 0.8;

    let rectShape = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height / 2,
        width: end[0] - start[0],
        height: height,
      },
      {
        x: params.coordSys.x,
        y: params.coordSys.y,
        width: params.coordSys.width,
        height: params.coordSys.height,
      }
    );

    return (
      rectShape && {
        type: "rect",
        transition: ["shape"],
        shape: rectShape,
        style: api.style(),
      }
    );
  }

  const getOptions = () => ({
    tooltip: {
      formatter: function (params: { marker: any; name: any; value: (number | EquipmentInteractionData | undefined)[] }) {
        return ReactDomServer.renderToStaticMarkup(<TooltipHeatmap params={params} />);
      },
    },
    // dataZoom: [
    //   {
    //     type: "slider",
    //     filterMode: "weakFilter",
    //     showDataShadow: false,
    //     top: 0,
    //     labelFormatter: "",
    //   },
    // ],
    grid: {
      left: "0%",
      right: "0%",
      bottom: "0%",
      top: "20px",
      containLabel: true,
      height: 30 * days!.length! + 8,
    },
    xAxis: {
      position: "top",
      min: grpahStart,
      max: grpahEnd,
      interval: 3600,
      scale: true,
      axisLabel: {
        formatter: function (val: number) {
          return DateTime.local().startOf("day").plus({ seconds: val }).toFormat("HH:mm");
        },
      },
    },
    yAxis: [
      {
        data: days!,
        position: "right",
        axisLabel: {
          formatter: function (val: string) {
            return DateTime.fromSQL(val).toFormat("dd.MM");
          },
          width: 100,
          overflow: "break"
        },
      },
    ],
    series: [
      {
        type: "custom",
        renderItem: renderItem,
        itemStyle: {
          opacity: 0.8,
        },
        encode: {
          x: [1, 2],
          y: 0,
        },
        data: timelines,
      },
      {
        type: "line",
        animation: false,
        markLine: {
          silent: true,
          lineStyle: {
            color: "#222222",
          },
          label: {
            show: false,
          },
          symbol: "none",
          data: shiftMarkers,
        },
      },
      // {
      //   type: "line",
      //   animation: false,
      //   markLine: {
      //     silent: true,
      //     lineStyle: {
      //       color: "blue",
      //     },
      //     label: {
      //       formatter: function (val: { value: number }) {
      //         return DateTime.local()
      //           .startOf("day")
      //           .plus({ seconds: Number(val.value) })
      //           .toFormat("HH:mm");
      //       },
      //       rotate: 90,
      //       position: "end",
      //       distance: 20,
      //       backgroundColor: "blue",
      //       borderWidth: 4,
      //       borderRadius: 0,
      //       borderColor: "blue",
      //       color: "white",
      //       fontWeight: 600,
      //     },
      //     symbol: "none",
      //     data: markersData,
      //   },
      // },
    ],
  });

  function resetZoom() {
    const echartInstance = ref?.getEchartsInstance();
    echartInstance?.dispatchAction({
      type: "dataZoom",
      start: 0, // percentage of starting position; 0 - 100
      end: 100, // percentage of ending position; 0 - 100
    });
  }

  function saveHeatMap() {
    const echartInstance = ref?.getEchartsInstance();
    const base64 = echartInstance?.getDataURL();
    var link = document.createElement("a");
    link.download = "Chart";
    link.href = base64!;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }



  return (
    loading ? <></> : <ReactEcharts
      option={getOptions()}
      showLoading={loading}
      loadingOption={{
        text: '',
        color: hslColor.primary,
        // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
        // maskColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
        zlevel: 0,
        // Font size. Available since `v4.8.0`.
        fontSize: 12,
        // Show an animated "spinner" or not. Available since `v4.8.0`.
        showSpinner: true,
        // Radius of the "spinner". Available since `v4.8.0`.
        spinnerRadius: 8,
        // Line width of the "spinner". Available since `v4.8.0`.
        lineWidth: 2,
        // Font thick weight. Available since `v5.0.1`.
        fontWeight: 'normal',
        // Font style. Available since `v5.0.1`.
        fontStyle: 'normal',
        // Font family. Available since `v5.0.1`.
        fontFamily: 'sans-serif'
      }}
      ref={(e) => {
        setRef(e);
      }}
      style={{ width: "100%", height: 30 * days.length + 30}}
      opts={{ renderer: "canvas" }}
    />
  );
};

function calculateData(filteredData: DataRow[], days: string[]) {
  let data: { name: string | number; value: (string | number)[]; itemStyle: { color: string; }; stack: string}[] = []

  Object.entries(_.groupBy(filteredData, "dateShift.start_day")).map((day) => {
    let index = days!.map(d => DateTime.fromSQL(d).toISO()).indexOf(day[0]);
    day[1]
      .map((d) =>
        d.events!.map((event) => (
          data.push({
            name: event[1],
            value: [
              index,
              Number(event[4]) - d.dateShift.start_day.toSeconds(),
              Number(event[4]) - d.dateShift.start_day.toSeconds() + Number(event[7]),
              Number(event[7]),
              event[4],
              event[6]
            ],
            stack: String(event[0]),
            itemStyle: { color: states.find((s) => s.value === event[8])?.color! }
          })
        ))
      )
  })
  return data
}

const TooltipHeatmap = ({ params }: { params: { name: any; value: (number | EquipmentInteractionData | undefined)[] } }) => {
  return (
    <div className="flex flex-col w-[16rem]">
      <div className="capitalize">{t(`${params.name}`)}</div>
      <div className="capitalize">{DateTime.fromSeconds(Number(params.value[4])).toFormat("HH:mm:ss")}</div>
      <div className="capitalize">{DateTime.fromSeconds(Number(params.value[5])).toFormat("HH:mm:ss")}</div>
      <div className="capitalize">
        {(params.value[9] as EquipmentInteractionData)?.personnel?.last_name}{" "}
        {(params.value[9] as EquipmentInteractionData)?.personnel?.first_name}
      </div>
    </div>
  );
};

export default EGantt;


// export function getHeatMapByDayPdf(state: AccData, tab: Tab, data: EquipmentReportTableData[], equipments: Equipment[], persons: Person[]) {
//   const filters = state.block?.filters
//   let filteredData = data.filter(row => 
//     filters?.days.includes(row.at.toISODate()!) &&
//     filters?.weekdays.includes(String(row.at.weekday)) &&
//     filters?.shifts.includes(row.shift.id) &&
//     filters?.equipments.includes(row.equipment_item_id) &&
//     (state.block?.options?.active_rows ? row.worked > 600 : true)
//   )
//   let excludeFilters: ExcludeFilters = getExcludeFilters(data, filteredData)

//   let blockDoc = state.doc
//   let stateConfig = state.config

//   let domBlock =  document.getElementById(`block-${state.block?.id}`)?.getElementsByClassName("echarts-for-react").item(0)! as HTMLElement
//   let echartInstance = echarts.getInstanceByDom(domBlock)
//   let graphHeight = echartInstance?.getHeight()! * 0.16
//   let base64 = echartInstance?.getDataURL();

//   let label = splitTextAndGetHeight(blockDoc, state.block?.name ? state.block?.name : "", 150)
//   let subLabelText = splitTextAndGetHeight(blockDoc, `${blocksTypes.find(x => x.value === state.block?.type)?.label} ${state.block?.options?.active_rows ? t('active_rows') : ""}`, 150)
//   let excludesText = splitTextAndGetHeight(blockDoc, getExcludeDataText(excludeFilters, equipments), 150)

//   if(stateConfig.currentHeight + subLabelText.height + 0.5 + excludesText.height + 0.5 + graphHeight * 0.75 > state.config.docHeight - 20) {
//     blockDoc.addPage()  
//     stateConfig.currentHeight = 20
//   }

//   // Label
//   blockDoc.setFont("Roboto", "medium")
//   blockDoc.setFontSize(state.config.labelTextSize)
//   blockDoc.text(label.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += label.height + 2

//   // SubLabel
//   blockDoc.setFont("Roboto", "light")
//   blockDoc.setFontSize(state.config.hintTextSize)
//   blockDoc.text(subLabelText.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += subLabelText.height + 0.5

//   // Excludes
//   blockDoc.setFont("Roboto", "light")
//   blockDoc.setFontSize(state.config.hintTextSize)
//   blockDoc.text(excludesText.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += excludesText.height + 0.5

//   blockDoc.addImage(base64!, "jpeg", 10, stateConfig.currentHeight, 190, graphHeight)

//   stateConfig.currentHeight += graphHeight + 6

//   return {...state, doc: blockDoc, config: stateConfig}
// }